import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, Mail, MapPin, Clock, Instagram, Link2, MessageCircle, ShieldCheck, ExternalLink, Building2, FileCheck2 } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { seoHead, SITE_ORIGIN, localBusinessSchema } from "@/lib/seo";
import { trackEvent } from "@/lib/tracking";

const PATH = "/official-business-information";
const TITLE = "Official Business Information | Woodseer Autos East London";
const DESCRIPTION =
  "Official contact, address, opening hours, MOT station number and verification details for Woodseer Autos — an independent garage and DVSA-authorised MOT station in Poplar, East London.";

const ADMIN_EMAIL = "woodseerautos@gmail.com";

export const Route = createFileRoute("/official-business-information")({
  head: () => ({
    ...seoHead({
      path: PATH,
      title: TITLE,
      description: DESCRIPTION,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Official Business Information", path: PATH },
      ],
      jsonLd: [localBusinessSchema()],
    }),
  }),
  component: OfficialBusinessInformationPage,
});

function OfficialBusinessInformationPage() {
  const biz = getBusinessConfig();

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
        <header className="mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Official Record</p>
          <h1 className="mt-3 text-3xl md:text-5xl font-extrabold text-foreground tracking-tight">
            Official Business Information
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            This page is the public, authoritative record of Woodseer Autos — used for Google
            Business Profile, Bing Places, directory citations, customer verification and ownership
            requests. All details below match our DVSA records, signage and domain email.
          </p>
        </header>

        {/* Identity */}
        <Section icon={Building2} title="Official business identity">
          <Row label="Business name" value={biz.name} />
          <Row label="Trading name" value="Woodseer Autos" />
          <Row label="Business type" value="Independent garage, MOT station and vehicle repair workshop" />
          <Row label="Services" value="MOT testing, servicing, diagnostics, mechanical repairs, bodywork and related garage services" />
          <Row label="MOT station (VTS) number" value={biz.vtsNumber} />
          <Row label="MOT classes" value="1, 2, 3, 4, 5 and 7" />
        </Section>

        {/* Address */}
        <Section icon={MapPin} title="Official address">
          <Row label="Registered / customer address" value={`${biz.address}, ${biz.addressLine2}, ${biz.postcode}`} />
          <Row label="Yard entrance (for vehicles)" value="14 Copenhagen Place, London, E14 7DF" />
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed border-l-2 border-primary/40 pl-4">
            The registered address is <strong className="text-foreground">16 Pixley Street</strong>.
            Customers driving to the workshop should sat-nav to the yard entrance at
            <strong className="text-foreground"> 14 Copenhagen Place, E14 7DF</strong>.
          </p>
          <a
            href={biz.googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("directions_click", { location: "official_business_info" })}
            className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-primary/80"
          >
            Directions to the yard entrance <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Section>

        {/* Contact */}
        <Section icon={Phone} title="Official contact details">
          <Row label="Phone" value={biz.phone} href={biz.phoneHref} />
          <Row label="Main customer email" value={biz.email} href={`mailto:${biz.email}`} />
          <Row label="Backup / admin email" value={ADMIN_EMAIL} href={`mailto:${ADMIN_EMAIL}`} hint="Used for platform / verification correspondence" />
          <Row label="Website" value="https://woodseerautos.co.uk" href="https://woodseerautos.co.uk" />
          <Row label="Instagram" value="@woodseerautos" href={biz.instagramUrl} external />
          <Row label="Linktree" value="linktr.ee/woodseerautos" href={biz.linktreeUrl} external />
        </Section>

        {/* Hours */}
        <Section icon={Clock} title="Opening hours">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {biz.hours.map((h) => (
              <div key={h.day} className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2">
                <dt className="text-sm font-semibold text-foreground">{h.day}</dt>
                <dd className="text-sm text-muted-foreground tabular-nums">{h.time}</dd>
              </div>
            ))}
          </dl>
        </Section>

        {/* Verification */}
        <Section icon={ShieldCheck} title="Verification and ownership requests">
          <p className="text-sm text-foreground leading-relaxed">
            Woodseer Autos uses this website, the domain email{" "}
            <a href={`mailto:${biz.email}`} className="text-primary font-semibold underline">{biz.email}</a>
            {" "}and the backup admin email{" "}
            <a href={`mailto:${ADMIN_EMAIL}`} className="text-primary font-semibold underline">{ADMIN_EMAIL}</a>
            {" "}for business verification, customer enquiries and platform access requests
            (including Google Business Profile ownership claims).
          </p>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            This page exists as a public reference for Google, Bing, Apple Maps, Yell and other
            citation providers to confirm our identity, NAP details and operating address.
          </p>
        </Section>

        {/* Authority links */}
        <Section icon={FileCheck2} title="Official channels & profiles">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <AuthorityLink href={biz.googleReviewsUrl} label="Google Reviews" sub="Read or leave a review" track="google_profile_click" />
            <AuthorityLink href={biz.instagramUrl} label="Instagram" sub="@woodseerautos" icon={Instagram} track="instagram_click" />
            <AuthorityLink href={biz.linktreeUrl} label="Linktree" sub="All Woodseer Autos links" icon={Link2} track="linktree_click" />
            <AuthorityLink href={biz.whatsappUrl} label="WhatsApp" sub="Message the workshop" icon={MessageCircle} track="whatsapp_click" />
          </div>
        </Section>

        {/* CTA strip */}
        <div className="mt-12 rounded-2xl border border-border bg-card p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold text-foreground">Need to reach the workshop?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Call during workshop hours, message us on WhatsApp, or request a booking online.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={biz.phoneHref} onClick={() => trackEvent("call_click", { location: "official_business_info" })} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90">
              <Phone className="h-4 w-4" /> {biz.phone}
            </a>
            <a href={biz.whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("whatsapp_click", { location: "official_business_info" })} className="inline-flex items-center gap-2 rounded-md border border-border bg-secondary px-4 py-2.5 text-sm font-bold text-secondary-foreground hover:bg-secondary/80">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <Link to="/book" className="inline-flex items-center gap-2 rounded-md border border-border bg-secondary px-4 py-2.5 text-sm font-bold text-secondary-foreground hover:bg-secondary/80">
              Request a booking
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-md border border-border bg-secondary px-4 py-2.5 text-sm font-bold text-secondary-foreground hover:bg-secondary/80">
              Contact page
            </Link>
          </div>
          <div className="mt-6 pt-5 border-t border-border/60 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-sm">
            <Link to="/mot" className="text-foreground hover:text-primary font-semibold">MOT</Link>
            <Link to="/servicing" className="text-foreground hover:text-primary font-semibold">Servicing</Link>
            <Link to="/diagnostics" className="text-foreground hover:text-primary font-semibold">Diagnostics</Link>
            <Link to="/mechanical-repairs" className="text-foreground hover:text-primary font-semibold">Mechanical Repairs</Link>
            <Link to="/bodywork" className="text-foreground hover:text-primary font-semibold">Bodywork</Link>
            <Link to="/areas" className="text-foreground hover:text-primary font-semibold">Areas</Link>
            <Link to="/blog" className="text-foreground hover:text-primary font-semibold">Blog</Link>
            <a href={`${SITE_ORIGIN}/sitemap.xml`} className="text-foreground hover:text-primary font-semibold">Sitemap</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-8 rounded-xl border border-border bg-card p-5 md:p-6">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary border border-primary/20">
          <Icon className="h-4 w-4" />
        </span>
        <h2 className="text-lg md:text-xl font-bold text-foreground">{title}</h2>
      </div>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function Row({
  label,
  value,
  href,
  external,
  hint,
}: {
  label: string;
  value: string;
  href?: string;
  external?: boolean;
  hint?: string;
}) {
  const content = href ? (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="text-primary font-semibold hover:underline break-words"
    >
      {value}
    </a>
  ) : (
    <span className="text-foreground font-semibold break-words">{value}</span>
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-1 sm:gap-4 py-2 border-b border-border/40 last:border-0">
      <dt className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm">
        {content}
        {hint && <span className="block mt-0.5 text-[11px] text-muted-foreground">{hint}</span>}
      </dd>
    </div>
  );
}

function AuthorityLink({
  href,
  label,
  sub,
  icon: Icon = ExternalLink,
  track,
}: {
  href: string;
  label: string;
  sub: string;
  icon?: React.ComponentType<{ className?: string }>;
  track: string;
}) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent(track as Parameters<typeof trackEvent>[0], { location: "official_business_info" })}
      className="flex items-center gap-3 rounded-lg border border-border bg-background hover:bg-secondary/40 px-4 py-3 transition-colors"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary border border-primary/20">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-bold text-foreground">{label}</span>
        <span className="block text-xs text-muted-foreground truncate">{sub}</span>
      </span>
    </a>
  );
}
